import { motion, useReducedMotion } from "framer-motion";
import { SCHEDULE_DAYS } from "@/data/agenticHackathon";
import { defaultTransition, fadeUp, viewport } from "@/lib/motionVariants";

function ScheduleRow({ day, reduceMotion }) {
    const colClass =
        day.items.length >= 7
            ? "lg:grid-cols-7"
            : day.items.length >= 4
              ? "lg:grid-cols-4"
              : "lg:grid-cols-1";

    const isPreActivity = day.id === "pre-activity";

    return (
        <div className="mb-14 sm:mb-16 last:mb-0">
            <div className="mb-6 sm:mb-8">
                <p
                    className={`text-xs font-mono uppercase tracking-[0.2em] mb-2 ${
                        isPreActivity ? "text-emerald-700" : "text-[#E54D2E]"
                    }`}
                >
                    {day.label}
                </p>
                {day.tagline ? (
                    <p className="text-sm text-zinc-500 max-w-2xl">{day.tagline}</p>
                ) : null}
            </div>
            <div className="relative">
                <div
                    className={`hidden lg:block absolute top-[18px] left-[8%] right-[8%] h-px ${
                        isPreActivity ? "bg-emerald-600/35" : "bg-[#E54D2E]/35"
                    }`}
                    aria-hidden
                />
                <ul
                    className={`flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 sm:-mx-0 sm:px-0 lg:grid ${colClass} lg:gap-3 lg:overflow-visible lg:pb-0 scrollbar-thin`}
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    {day.items.map((block, index) => (
                        <motion.li
                            key={`${day.id}-${block.time}-${block.title}`}
                            className={`snap-start shrink-0 w-[min(260px,78vw)] sm:w-[240px] lg:w-auto lg:shrink flex flex-col motion-card rounded-2xl p-3 -m-3 ${
                                block.accent
                                    ? "ring-1 ring-emerald-600/20 bg-emerald-50/50"
                                    : ""
                            }`}
                            initial={reduceMotion ? false : "hidden"}
                            whileInView={reduceMotion ? undefined : "visible"}
                            viewport={viewport}
                            variants={fadeUp}
                            transition={{
                                ...defaultTransition,
                                delay: index * 0.06,
                            }}
                            whileHover={
                                reduceMotion
                                    ? undefined
                                    : { y: -4, scale: 1.02 }
                            }
                        >
                            <div className="flex items-center gap-2 lg:flex-col lg:items-start lg:gap-0">
                                <span
                                    className={`h-3 w-3 shrink-0 rounded-full ring-4 ring-[#FAFAFA] lg:mb-3 ${
                                        block.accent || isPreActivity
                                            ? "bg-emerald-600"
                                            : "bg-[#E54D2E]"
                                    }`}
                                    aria-hidden
                                />
                                <time
                                    className={`font-mono text-xs sm:text-sm font-medium whitespace-nowrap ${
                                        block.accent || isPreActivity
                                            ? "text-emerald-700"
                                            : "text-[#E54D2E]"
                                    }`}
                                >
                                    {block.time}
                                </time>
                                {index < day.items.length - 1 ? (
                                    <span
                                        className="lg:hidden ml-auto text-zinc-300 font-light"
                                        aria-hidden
                                    >
                                        →
                                    </span>
                                ) : null}
                            </div>
                            <h3 className="mt-3 font-display text-base sm:text-lg font-bold text-zinc-950 leading-snug">
                                {block.title}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                                {block.detail}
                            </p>
                        </motion.li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default function ScheduleTimeline() {
    const reduceMotion = useReducedMotion();

    return (
        <div className="relative w-full">
            {SCHEDULE_DAYS.map((day) => (
                <ScheduleRow
                    key={day.id}
                    day={day}
                    reduceMotion={reduceMotion}
                />
            ))}
            <p className="mt-2 text-center text-xs text-zinc-400 lg:hidden">
                Swipe each row to see the full schedule →
            </p>
        </div>
    );
}
